import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import CanonicalTag from '@/components/CanonicalTag';
import MetaTags from '@/components/MetaTags';
import Breadcrumbs from '@/components/Breadcrumbs';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from 'react-router-dom';
import { useDoctors } from '@/hooks/useDoctors';
import { ArrowLeft, Stethoscope, MapPin, Clock, Phone, Search, Award } from 'lucide-react';
import { buildDoctorItemListSchema, upsertDoctorItemListSchema } from '@/lib/doctorSchema';
const UrologyDoctors = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const { doctors: urologyDoctors, loading, error } = useDoctors({
        selectedSpecialty: 'اورولوژی'
    });
    const filteredDoctors = (() => {
        const query = searchQuery.trim().toLowerCase();
        const searched = query.length === 0
            ? urologyDoctors
            : urologyDoctors.filter(doctor => doctor.name.toLowerCase().includes(query) ||
                (doctor.specialty || '').toLowerCase().includes(query) ||
                (doctor.address || '').toLowerCase().includes(query));
        return searched.sort((a, b) => {
            if (a.isFeatured && !b.isFeatured)
                return -1;
            if (!a.isFeatured && b.isFeatured)
                return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
    })();
    useEffect(() => {
        if (!loading && filteredDoctors.length > 0) {
            const schema = buildDoctorItemListSchema({
                path: '/doctors/urology',
                pageTitle: 'پزشک اورولوژی شهرقدس | بهترین متخصص کلیه و مثانه | آزمایشگاه سلامت',
                pageDescription: 'بهترین پزشکان متخصص اورولوژی در شهرقدس. درمان سنگ کلیه، پروستات، عفونت ادراری و مشکلات دستگاه ادراری تناسلی. نوبت آنلاین',
                medicalSpecialty: 'Urology',
                doctors: filteredDoctors,
            });
            return upsertDoctorItemListSchema(schema);
        }
    }, [filteredDoctors, loading]);
    if (loading) {
        return (<div className="min-h-screen bg-light-gradient flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-32 h-32 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
            <div className="absolute inset-0 w-32 h-32 border-4 border-transparent border-t-purple-600 rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-700 mt-8 mb-4">در حال بارگذاری پزشکان اورولوژی...</h3>
          <p className="text-gray-500">لطفاً صبر کنید</p>
        </div>
      </div>);
    }
    if (error) {
        return (<div className="min-h-screen bg-light-gradient flex items-center justify-center">
        <div className="text-center">
          <div className="w-32 h-32 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <div className="text-red-500 text-6xl">⚠️</div>
          </div>
          <h2 className="text-3xl font-bold text-red-600 mb-4">خطا در بارگذاری</h2>
          <p className="text-gray-600 text-lg mb-6">{error}</p>
          <Button onClick={() => window.location.reload()} className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all duration-300">
            تلاش مجدد
          </Button>
        </div>
      </div>);
    }
    return (<div className="min-h-screen bg-light-gradient">
      <CanonicalTag path="/doctors/urology"/>
      <MetaTags title="پزشک اورولوژی شهرقدس | بهترین متخصص کلیه و مثانه | آزمایشگاه سلامت" description="بهترین پزشکان متخصص اورولوژی در شهرقدس. درمان سنگ کلیه، پروستات، عفونت ادراری و مشکلات دستگاه ادراری تناسلی. نوبت آنلاین" keywords="پزشک اورولوژی شهرقدس, متخصص کلیه شهرقدس, دکتر اورولوژی قدس, سنگ کلیه, پروستات, عفونت ادراری, متخصص مثانه شهرقدس, اورولوژیست قدس" ogImage="https://www.salamatlab.com/urology-doctors-preview.jpg" path="/doctors/urology"/>
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        
        <div className="mb-6">
          <Breadcrumbs />
        </div>
        
        <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white rounded-3xl p-8 md:p-12 mb-12 shadow-2xl">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="bg-white/20 p-4 rounded-full">
                <Stethoscope className="w-12 h-12 text-white"/>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              بهترین پزشکان اورولوژی شهرقدس
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100">
              درمان تخصصی بیماری‌های کلیه، مثانه، پروستات و دستگاه ادراری تناسلی
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/10 p-4 rounded-xl">
                <Award className="w-8 h-8 mx-auto mb-2"/>
                <h3 className="font-bold">متخصص مجرب</h3>
                <p className="text-sm text-blue-100">پزشکان با سال‌ها تجربه</p>
              </div>
              <div className="bg-white/10 p-4 rounded-xl">
                <MapPin className="w-8 h-8 mx-auto mb-2"/>
                <h3 className="font-bold">دسترسی آسان</h3>
                <p className="text-sm text-blue-100">در قلب شهرقدس</p>
              </div>
              <div className="bg-white/10 p-4 rounded-xl">
                <Clock className="w-8 h-8 mx-auto mb-2"/>
                <h3 className="font-bold">نوبت‌دهی سریع</h3>
                <p className="text-sm text-blue-100">ساعات کاری مناسب</p>
              </div>
            </div>
          </div>
        </div>

        
        <Card className="p-6 mb-8 border-0 shadow-xl bg-white/90 backdrop-blur-sm rounded-2xl">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center flex items-center justify-center">
              <Search className="w-5 h-5 ml-2 text-blue-600"/>
              جستجوی پزشک اورولوژی
            </h3>
            <div className="relative">
              <input type="text" placeholder="جستجو بر اساس نام پزشک یا آدرس..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full px-6 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-300"/>
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                🔍
              </div>
            </div>
          </div>
        </Card>

        
        <div className="mb-6">
          <Button asChild variant="outline" className="flex items-center gap-2 hover:bg-blue-50">
            <Link to="/doctors">
              <ArrowLeft className="w-4 h-4 rtl:rotate-180"/>
              بازگشت به لیست کل پزشکان
            </Link>
          </Button>
        </div>

        
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            پزشکان متخصص اورولوژی در شهرقدس ({filteredDoctors.length} پزشک)
          </h2>
          <p className="text-gray-600">
            لیست کاملی از بهترین پزشکان متخصص اورولوژی در شهرقدس برای درمان بیماری‌های کلیه، مثانه و دستگاه ادراری
          </p>
        </div>

        
        {filteredDoctors.length === 0 ? (<Card className="p-12 text-center border-0 shadow-xl bg-white/90 backdrop-blur-sm rounded-2xl">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-gray-700 mb-4">پزشک اورولوژی یافت نشد</h3>
            <p className="text-gray-600 mb-6">
              متاسفانه در حال حاضر پزشک اورولوژی با این مشخصات در شهرقدس در دسترس نیست
            </p>
            <Button asChild className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-3 rounded-xl hover:shadow-lg transition-all duration-300">
              <Link to="/doctors">مشاهده سایر پزشکان</Link>
            </Button>
          </Card>) : (<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {filteredDoctors.map((doctor, index) => (<Card key={doctor.licenseNumber} className="group relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-white rounded-3xl" style={{ animationDelay: `${index * 0.1}s` }}>
                
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 via-indigo-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="relative z-10 p-8">
                  
                  <div className="flex items-start space-x-6 space-x-reverse mb-6">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-700 transition-colors duration-300">
                        {doctor.name}
                      </h3>
                      <Badge className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium border-0 mb-2">
                        {doctor.specialty}
                      </Badge>
                      {doctor.description && (<p className="text-gray-600 text-sm mt-2">{doctor.description}</p>)}
                    </div>
                  </div>

                  
                  <div className="mb-6">
                    <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-2xl border border-gray-100">
                      <div className="text-gray-600 text-sm mb-1">شماره نظام:</div>
                      <div className="text-gray-800 font-bold">{doctor.licenseNumber}</div>
                    </div>
                  </div>

                  
                  <div className="mb-6 bg-gradient-to-r from-gray-50 to-green-50 p-4 rounded-2xl border border-gray-100">
                    <div className="flex items-start">
                      <MapPin className="w-5 h-5 text-blue-500 ml-3 mt-0.5 flex-shrink-0"/>
                      <p className="text-gray-700 text-sm leading-relaxed">{doctor.address}</p>
                    </div>
                  </div>

                  
                  {doctor.workingHours && (<div className="mb-6 bg-gradient-to-r from-gray-50 to-orange-50 p-4 rounded-2xl border border-gray-100">
                      <div className="flex items-center">
                        <Clock className="w-5 h-5 text-green-500 ml-3 flex-shrink-0"/>
                        <span className="text-gray-700 text-sm font-medium">{doctor.workingHours}</span>
                      </div>
                    </div>)}

                  
                  {doctor.phones && doctor.phones.length > 0 && (<div className="mb-6">
                      <div className="flex flex-wrap gap-3">
                        {doctor.phones.map((phone, phoneIndex) => (<a key={phoneIndex} href={`tel:${phone}`} className="text-blue-600 hover:text-blue-700 text-sm font-semibold bg-gradient-to-r from-blue-100 to-indigo-100 hover:from-blue-200 hover:to-indigo-200 px-4 py-3 rounded-xl transition-all duration-300 flex items-center hover:scale-105 hover:shadow-md">
                            <Phone className="w-4 h-4 ml-2"/>
                            {phone}
                          </a>))}
                      </div>
                    </div>)}

                  
                </div>
              </Card>))}
          </div>)}

        
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="p-8 border-0 shadow-xl bg-white/90 backdrop-blur-sm rounded-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <Award className="w-6 h-6 ml-3 text-blue-600"/>
              چرا پزشک اورولوژی در شهرقدس؟
            </h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>درمان تخصصی سنگ کلیه و مجاری ادراری</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>تشخیص و درمان بیماری‌های پروستات</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>درمان عفونت‌های دستگاه ادراری</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>جراحی‌های کم تهاجمی ادراری</span>
              </li>
            </ul>
          </Card>

          <Card className="p-8 border-0 shadow-xl bg-white/90 backdrop-blur-sm rounded-2xl">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <MapPin className="w-6 h-6 ml-3 text-green-600"/>
              مزایای انتخاب پزشک در شهرقدس
            </h2>
            <ul className="space-y-4 text-gray-700">
              <li className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>دسترسی آسان از تمام نقاط تهران</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>پارکینگ رایگان و مناسب</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>نزدیکی به آزمایشگاه‌های تشخیص پزشکی</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 ml-3 flex-shrink-0"></div>
                <span>هزینه‌های مناسب‌تر نسبت به مرکز تهران</span>
              </li>
            </ul>
          </Card>
        </div>
      </main>
    </div>);
};
export default UrologyDoctors;
